/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useEffect, useRef, useSyncExternalStore } from "react";

// Cross-component "focus" bus: the sidebar asks a tab to select a specific
// device (by scope + id). It works whether the target tab is already mounted
// (via the event) or about to mount (via a pending value in sessionStorage).

const KEY = (scope: string) => `nyrvex-focus-${scope}`;

export function requestFocus(scope: string, id: string | number): void {
  try {
    sessionStorage.setItem(KEY(scope), String(id));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent("nyrvex:focus", { detail: { scope, id: String(id) } }));
}

/** Read and clear a pending focus request for a scope (used on tab mount). */
export function consumeFocus(scope: string): string | null {
  try {
    const v = sessionStorage.getItem(KEY(scope));
    if (v) sessionStorage.removeItem(KEY(scope));
    return v;
  } catch {
    return null;
  }
}

/**
 * Subscribe a tab to focus requests for its scope. `cb` is kept in a ref so it
 * always sees the latest state without re-subscribing. Fires immediately for a
 * pending request left in sessionStorage when the tab mounts.
 */
export function useFocus(scope: string, cb: (id: string) => void): void {
  const cbRef = useRef(cb);
  cbRef.current = cb;
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail as { scope?: string; id?: string } | undefined;
      if (d?.scope === scope && d.id != null) cbRef.current(d.id);
    };
    window.addEventListener("nyrvex:focus", handler);
    const pending = consumeFocus(scope);
    if (pending) cbRef.current(pending);
    return () => window.removeEventListener("nyrvex:focus", handler);
  }, [scope]);
}

// ---- what is open right now -------------------------------------------------
// The mirror image of the focus bus: a tab reports which device/tool it is
// showing, the sidebar highlights it. Unmounting clears the report.

let selection: Record<string, string | null> = {};
const listeners = new Set<() => void>();

export function setSelected(scope: string, id: string | number | null): void {
  const next = id == null ? null : String(id);
  if (selection[scope] === next || (next === null && !(scope in selection))) return;
  selection = { ...selection, [scope]: next };
  listeners.forEach((l) => l());
}

/** Snapshot of every scope's open item (a new object on each change). */
export function useSelection(): Record<string, string | null> {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => selection,
    () => selection,
  );
}

/** Report this tab's open item for as long as it is mounted. */
export function useReportSelected(scope: string, id: string | number | null | undefined): void {
  const value = id == null ? null : String(id);
  useEffect(() => {
    setSelected(scope, value);
    return () => setSelected(scope, null);
  }, [scope, value]);
}
